import { handle400Statuses, sendApiRequest } from './api';
import { logNetRequest } from '@utils/usefulFuncs';
import { advancedSettingsActionTypes } from '@constants/actionTypes';
import {
  ADVANCED_SETTING_VALUE_TYPES,
  hasFreshAdvancedSettingsForMode,
  setAdvancedSettingsForMode,
  getAdvancedSettingsForMode,
} from '@constants/advancedSettings';
import { resolveGptModelId } from '@constants/ai';

const FIELD_TYPE_TO_VALUE_TYPE = {
  on_off: ADVANCED_SETTING_VALUE_TYPES.onOff,
  bool: ADVANCED_SETTING_VALUE_TYPES.bool,
  integer: ADVANCED_SETTING_VALUE_TYPES.integer,
  float: ADVANCED_SETTING_VALUE_TYPES.float,
  string: ADVANCED_SETTING_VALUE_TYPES.string,
  date: ADVANCED_SETTING_VALUE_TYPES.date,
  custom: ADVANCED_SETTING_VALUE_TYPES.custom,
};

function buildUrl(modeType) {
  return `v2/genai/modes/${encodeURIComponent(modeType)}/advanced_settings`;
}

function coerceValueToLegacyState(fieldType, value) {
  if (value === null || value === undefined) {
    if (fieldType === 'bool') return undefined;
    return '';
  }
  switch (fieldType) {
    case 'on_off':
      return value;
    case 'bool':
      return Boolean(value);
    case 'integer':
    case 'float':
      return typeof value === 'number' ? String(value) : value;
    case 'date':
    case 'string':
    case 'custom':
    default:
      return value;
  }
}

export function adaptAdvancedSettingsResponse(settings) {
  const fields = [];
  const startingValues = {};
  const definitions = {};

  if (!Array.isArray(settings)) {
    return { fields, startingValues, definitions };
  }

  settings.forEach((entry) => {
    if (!entry || typeof entry !== 'object') return;
    const { field_type, api_param_name, name, value } = entry;
    if (!api_param_name) return;
    const valueType = FIELD_TYPE_TO_VALUE_TYPE[field_type];
    if (!valueType) {
      // forward-compat: ignore unknown field_type
      return;
    }
    let coerced = coerceValueToLegacyState(field_type, value);
    if (api_param_name === 'model' && coerced) {
      coerced = resolveGptModelId(coerced);
    }
    fields.push(api_param_name);
    startingValues[api_param_name] = coerced;
    definitions[api_param_name] = {
      keyName: api_param_name,
      valueType,
      defaultValue: coerced,
      formDisplayName: name,
    };
  });

  return { fields, startingValues, definitions };
}

export async function fetchAdvancedSettings(modeType) {
  const url = buildUrl(modeType);
  const { status, data, error } = await sendApiRequest('get', url);
  logNetRequest(url, status, data, error);
  handle400Statuses(status);

  if (status === 200 && Array.isArray(data?.advanced_settings)) {
    return { ok: true, status, settings: data.advanced_settings, error: null };
  }
  if (status === 404) {
    return { ok: true, status, settings: [], error: null };
  }
  return {
    ok: false,
    status,
    settings: [],
    error: data?.error || error || 'Unable to load advanced settings',
  };
}

export const loadAdvancedSettingsForMode = (mode, { force = false } = {}) => {
  return async (dispatch) => {
    if (!mode) return null;
    if (!force && hasFreshAdvancedSettingsForMode(mode)) {
      const cached = getAdvancedSettingsForMode(mode);
      dispatch({
        type: advancedSettingsActionTypes.setAdvancedSettingsLoaded,
        payload: { mode, fields: cached.fields, startingValues: cached.startingValues },
      });
      return { ok: true, fromCache: true, ...cached };
    }

    dispatch({
      type: advancedSettingsActionTypes.setAdvancedSettingsLoading,
      payload: { mode },
    });

    const result = await fetchAdvancedSettings(mode);

    if (!result.ok) {
      dispatch({
        type: advancedSettingsActionTypes.setAdvancedSettingsError,
        payload: { mode, error: result.error, status: result.status },
      });
      return result;
    }

    const adapted = adaptAdvancedSettingsResponse(result.settings);
    setAdvancedSettingsForMode(mode, adapted);
    const cached = getAdvancedSettingsForMode(mode);

    dispatch({
      type: advancedSettingsActionTypes.setAdvancedSettingsLoaded,
      payload: { mode, fields: cached.fields, startingValues: cached.startingValues },
    });

    return { ok: true, fromCache: false, ...cached };
  };
};
